import { useState, useEffect } from "react";
import { X, FileText, Zap, Loader2, RefreshCw, Download, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import DramaMeter from "./DramaMeter";
import Scratchpad from "./Scratchpad";
import { useShieldCredits } from "@/hooks/useShieldCredits";
import RefillModal from "./RefillModal";
import PaywallModal from "./PaywallModal";
import { useNavigate } from "react-router-dom";

interface Document {
  id: string;
  file_name: string;
  file_path: string;
  file_type: string;
  ai_summary: string | null;
  toxicity_score: number | null;
  smart_badges: string[];
}

interface IntelligencePanelProps {
  vaultDoc: Document;
  onClose: () => void;
  onCreditUsed?: () => void;
}

const IntelligencePanel = ({ vaultDoc, onClose, onCreditUsed }: IntelligencePanelProps) => {
  const navigate = useNavigate();
  const { credits, isPaidUser, deductCredit, refetch } = useShieldCredits();
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [summary, setSummary] = useState(vaultDoc.ai_summary || "");
  const [keyFacts, setKeyFacts] = useState<string[]>([]);
  const [toxicityScore, setToxicityScore] = useState(vaultDoc.toxicity_score);
  const [isDownloadingAnalysis, setIsDownloadingAnalysis] = useState(false);
  const [showRefillModal, setShowRefillModal] = useState(false);

  useEffect(() => {
    setSummary(vaultDoc.ai_summary || "");
    setToxicityScore(vaultDoc.toxicity_score);
    setKeyFacts([]);
  }, [vaultDoc]);

  const handleAnalyze = async () => {
    if (credits <= 0) {
      setShowRefillModal(true);
      return;
    }

    setIsAnalyzing(true);
    
    try {
      // Credit is deducted server-side by analyze-document


      const { data, error: invokeError } = await supabase.functions.invoke('analyze-document', {
        body: {
          documentId: vaultDoc.id,
          filePath: vaultDoc.file_path,
        },
      });

      if (invokeError) {
        throw new Error(invokeError.message || "Analysis failed");
      }
      setSummary(data.summary || "");
      setKeyFacts(data.keyFacts || []);
      setToxicityScore(data.toxicityScore);
      
      // Refresh document data
      const { data: updatedDoc } = await supabase
        .from("vault_documents")
        .select("*")
        .eq("id", vaultDoc.id)
        .single();
      
      if (updatedDoc) {
        setSummary(updatedDoc.ai_summary || "");
        setToxicityScore(updatedDoc.toxicity_score);
      }

      refetch();
      onCreditUsed?.();
      toast.success("Analysis complete!");
    } catch (error) {
      console.error("Analysis error:", error);
      toast.error(error instanceof Error ? error.message : "Analysis failed");
    } finally {
      setIsAnalyzing(false);
    }
  };


  const handleDownloadAnalysis = async () => {
    setIsDownloadingAnalysis(true);
    try {
      const analysisContent = `
GreyRock AI - Document Analysis Report
======================================
Document: ${vaultDoc.file_name}
Date: ${new Date().toLocaleDateString()}

SUMMARY
-------
${summary || "No summary available"}

KEY FACTS
---------
${keyFacts.length > 0 ? keyFacts.map((fact, i) => `${i + 1}. ${fact}`).join("\n") : "No key facts extracted"}

TOXICITY SCORE
--------------
${toxicityScore !== null ? `${toxicityScore}/10` : "Not analyzed"}

======================================
Generated by GreyRock AI
      `.trim();

      const blob = new Blob([analysisContent], { type: "text/plain" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${vaultDoc.file_name.replace(/\.[^/.]+$/, "")}_analysis.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success("Analysis downloaded!");
    } catch {
      toast.error("Failed to download analysis");
    } finally {
      setIsDownloadingAnalysis(false);
    }
  };

  return (
    <>
      <div className="w-[450px] shrink-0 glass-card flex flex-col h-full">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-glass-border">
          <div className="flex items-center gap-3 min-w-0">
            <div className="p-2 rounded-lg bg-cyan/10 shrink-0">
              <FileText className="w-5 h-5 text-cyan" />
            </div>
            <div className="min-w-0">
              <h3 className="font-semibold truncate">Intelligence Panel</h3>
              <p className="text-sm text-muted-foreground truncate">{vaultDoc.file_name}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-charcoal-light text-muted-foreground hover:text-foreground"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="analysis" className="flex-1 flex flex-col">
          <TabsList className="mx-4 mt-4 bg-charcoal-medium">
            <TabsTrigger value="analysis" className="flex-1 gap-2">
              <FileText className="w-4 h-4" />
              Analysis
            </TabsTrigger>
            <TabsTrigger value="neutralize" className="flex-1 gap-2">
              <Zap className="w-4 h-4" />
              Neutralize
            </TabsTrigger>
          </TabsList>

          <div className="flex-1 overflow-y-auto">
            {/* Analysis Tab */}
            <TabsContent value="analysis" className="p-4 space-y-4 m-0">
              {!summary ? (
                <div className="text-center py-8">
                  <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <p className="text-muted-foreground mb-4">
                    No analysis available yet
                  </p>
                  <button
                    onClick={handleAnalyze}
                    disabled={isAnalyzing}
                    className={cn(
                      "inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold",
                      "bg-gradient-to-r from-cyan to-cyan-glow text-primary-foreground",
                      "hover:scale-[1.02] transition-transform",
                      "disabled:opacity-50 disabled:pointer-events-none"
                    )}
                  >
                    {isAnalyzing ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <FileText className="w-5 h-5" />
                    )}
                    {isAnalyzing ? "Analyzing..." : "Analyze Document"}
                  </button>
                  <p className="text-xs text-muted-foreground mt-2">
                    Uses 1 Shield Credit
                  </p>
                </div>
              ) : (
                <>
                  {/* Summary */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-sm text-muted-foreground">Summary</h4>
                      <button
                        onClick={handleAnalyze}
                        disabled={isAnalyzing}
                        className="p-1.5 rounded-lg hover:bg-charcoal-light text-muted-foreground hover:text-cyan"
                      >
                        <RefreshCw className={cn("w-4 h-4", isAnalyzing && "animate-spin")} />
                      </button>
                    </div>
                    <div className="p-4 rounded-xl bg-charcoal-medium border border-glass-border">
                      <p className="text-sm leading-relaxed">{summary}</p>
                    </div>
                  </div>

                  {/* Key Facts */}
                  {keyFacts.length > 0 && (
                    <div>
                      <h4 className="font-medium text-sm text-muted-foreground mb-2">Key Facts</h4>
                      <ul className="space-y-2">
                        {keyFacts.map((fact, i) => (
                          <li
                            key={i}
                            className="flex items-start gap-2 p-3 rounded-lg bg-charcoal-medium border border-glass-border"
                          >
                            <span className="text-cyan font-mono text-sm">{i + 1}.</span>
                            <span className="text-sm">{fact}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Toxicity Score */}
                  {toxicityScore !== null && (
                    <div>
                      <h4 className="font-medium text-sm text-muted-foreground mb-2">Toxicity Level</h4>
                      <DramaMeter score={toxicityScore} />
                    </div>
                  )}

                  {/* Download Analysis Button */}
                  {summary && (
                    <button
                      onClick={handleDownloadAnalysis}
                      disabled={isDownloadingAnalysis}
                      className={cn(
                        "w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-medium",
                        "bg-charcoal-light hover:bg-charcoal-medium border border-glass-border",
                        "transition-colors disabled:opacity-50"
                      )}
                    >
                      {isDownloadingAnalysis ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Download className="w-4 h-4" />
                      )}
                      Download Analysis
                    </button>
                  )}
                </>
              )}
            </TabsContent>

            {/* Neutralize Tab */}
            <TabsContent value="neutralize" className="p-4 space-y-4 m-0">
              {/* Toxicity Score - matches Threat Level styling from Dashboard */}
              {toxicityScore !== null && (
                <div>
                  <h4 className="font-medium text-sm text-muted-foreground mb-2">Threat Level</h4>
                  <DramaMeter score={toxicityScore} />
                </div>
              )}

              {/* Transfer CTA */}
              <div className="text-center py-6">
                <Zap className="w-12 h-12 mx-auto mb-4 text-cyan opacity-70" />
                <p className="text-sm text-muted-foreground mb-1">
                  Run the full forensic analysis on this document's summary.
                </p>
                <p className="text-xs text-muted-foreground/60 mb-5">
                  1 credit per analysis.
                </p>
                <button
                  onClick={() => {
                    navigate("/analysis-result", {
                      state: {
                        message: summary || vaultDoc.file_name,
                        contextMode: "personal",
                      },
                    });
                  }}
                  disabled={!summary}
                  className={cn(
                    "inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm",
                    "bg-gradient-to-r from-cyan to-cyan-glow text-primary-foreground",
                    "hover:scale-[1.02] transition-transform",
                    "shadow-[0_0_20px_-4px_hsl(184_100%_50%_/_0.4)]",
                    "disabled:opacity-50 disabled:pointer-events-none"
                  )}
                >
                  <ArrowRight className="w-4 h-4" />
                  Transfer to Neutralizer Lab
                </button>
                {!summary && (
                  <p className="text-xs text-amber-400 mt-3 font-mono">
                    Analyze the document first
                  </p>
                )}
              </div>
            </TabsContent>
          </div>
        </Tabs>
      </div>

      {isPaidUser ? (
        <RefillModal 
          open={showRefillModal} 
          onOpenChange={setShowRefillModal} 
          currentCredits={credits}
        />
      ) : (
        <PaywallModal open={showRefillModal} onOpenChange={setShowRefillModal} />
      )}
    </>
  );
};

export default IntelligencePanel;
